import { useCallback } from 'react';
import Button from 'devextreme-react/button';
import { formatMediaDateModified, formatMediaFileSize } from '../mediaViewer/mediaViewerFormatters';
import type { MediaFileMetadata } from '../mediaViewer/mediaViewerMetadata';
import type { DocumentEditorKind } from './documentFileTypes';
import { downloadDocumentFile, shareDocumentPreviewLink } from './documentViewerActions';
import '../mediaViewer/MediaViewerChrome.css';

export interface DocumentViewerChromeBarProps {
  kind: DocumentEditorKind;
  mountId: string;
  relativePath: string;
  metadata: MediaFileMetadata | null;
  disabled?: boolean;
}

export function DocumentViewerChromeBar({
  kind,
  mountId,
  relativePath,
  metadata,
  disabled = false
}: DocumentViewerChromeBarProps) {
  const onDownload = useCallback(() => {
    void downloadDocumentFile(mountId, relativePath);
  }, [mountId, relativePath]);

  const onShare = useCallback(() => {
    void shareDocumentPreviewLink(kind, mountId, relativePath);
  }, [kind, mountId, relativePath]);

  const modifiedLabel = formatMediaDateModified(metadata?.dateModified);
  const sizeLabel = formatMediaFileSize(metadata?.size);

  return (
    <div className="media-viewer-chrome__bar" role="toolbar" aria-label="Document preview">
      <div className="media-viewer-chrome__meta" aria-label="File details">
        <span className="media-viewer-chrome__meta-item">
          <span className="media-viewer-chrome__meta-label">Modified</span>
          <span className="media-viewer-chrome__meta-value">{modifiedLabel}</span>
        </span>
        <span className="media-viewer-chrome__meta-sep" aria-hidden="true">
          ·
        </span>
        <span className="media-viewer-chrome__meta-item">
          <span className="media-viewer-chrome__meta-label">Size</span>
          <span className="media-viewer-chrome__meta-value">{sizeLabel}</span>
        </span>
      </div>
      <div className="media-viewer-chrome__actions">
        <Button
          icon="download"
          text="Download"
          stylingMode="text"
          hint="Download file"
          disabled={disabled}
          onClick={onDownload}
        />
        <Button
          icon="share"
          text="Share"
          stylingMode="text"
          hint="Share link to document preview"
          disabled={disabled}
          onClick={onShare}
        />
      </div>
    </div>
  );
}
